import { useState, useEffect, useRef, useCallback } from 'react';
import { useApp } from '@/contexts/AppContext';
import { FileText, Link2 } from 'lucide-react';

/**
 * Autocomplete suggestions for [[note linking]]
 */
export default function NoteLinkSuggestions({ editor, onSelect }) {
  const { notes } = useApp();
  const [show, setShow] = useState(false);
  const [query, setQuery] = useState('');
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const containerRef = useRef(null);

  // Filter notes based on query
  const filteredNotes = notes
    .filter((note) => !note.is_deleted && !note.is_archived)
    .filter((note) => 
      note.title?.toLowerCase().includes(query.toLowerCase())
    )
    .slice(0, 8);

  // Handle note selection - defined before useEffect that uses it
  const handleSelect = useCallback((note) => {
    if (!editor || !note) return;

    const { state } = editor;
    const { selection } = state;
    const { $from } = selection;
    
    // Find the [[ start position
    const textBefore = $from.parent.textContent.slice(0, $from.parentOffset);
    const match = textBefore.match(/\[\[([^\]]*?)$/);
    
    if (match) {
      const startOffset = $from.parentOffset - match[0].length;
      const startPos = $from.start() + startOffset;
      const endPos = $from.pos;
      
      // Replace [[query with [[note title]]
      editor
        .chain()
        .focus()
        .deleteRange({ from: startPos, to: endPos })
        .insertContent(`[[${note.title}]]`)
        .run();
    }
    
    setShow(false);
    onSelect?.(note);
  }, [editor, onSelect]);

  // Check for [[ pattern and show suggestions
  useEffect(() => {
    if (!editor) return;

    const checkForTrigger = () => {
      try {
        const { state } = editor;
        const { selection } = state;
        const { $from } = selection;
        
        // Get text before cursor
        const textBefore = $from.parent.textContent.slice(0, $from.parentOffset);
        
        // Check for [[ pattern
        const match = textBefore.match(/\[\[([^\]]*?)$/);
        
        if (match) {
          setQuery(match[1]);
          setShow(true);
          setSelectedIndex(0);
          
          // Get cursor position for popup placement
          const coords = editor.view.coordsAtPos($from.pos);
          setPosition({
            top: coords.bottom + 5,
            left: coords.left,
          });
        } else {
          setShow(false);
          setQuery('');
        }
      } catch (err) {
        // Ignore errors during typing
      }
    };

    // Listen for transaction updates
    const updateHandler = () => checkForTrigger();
    
    editor.on('transaction', updateHandler);
    editor.on('focus', updateHandler);

    return () => {
      editor.off('transaction', updateHandler);
      editor.off('focus', updateHandler);
    };
  }, [editor]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!show) return;

    const handleKeyDown = (e) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        e.stopPropagation();
        setSelectedIndex((prev) => 
          prev < filteredNotes.length - 1 ? prev + 1 : 0
        );
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        e.stopPropagation();
        setSelectedIndex((prev) => 
          prev > 0 ? prev - 1 : filteredNotes.length - 1
        );
      } else if (e.key === 'Enter' && filteredNotes.length > 0) {
        e.preventDefault();
        e.stopPropagation();
        const noteToSelect = filteredNotes[selectedIndex];
        if (noteToSelect) {
          handleSelect(noteToSelect);
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setShow(false);
      } else if (e.key === 'Tab' && filteredNotes.length > 0) {
        e.preventDefault();
        e.stopPropagation();
        const noteToSelect = filteredNotes[selectedIndex];
        if (noteToSelect) {
          handleSelect(noteToSelect);
        }
      }
    };

    // Use capture phase to intercept before editor
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [show, filteredNotes, selectedIndex, handleSelect]);

  if (!show || filteredNotes.length === 0) return null;

  return (
    <div
      ref={containerRef}
      className="fixed z-50 w-64 max-h-64 overflow-y-auto bg-popover border border-border rounded-lg shadow-xl"
      style={{ top: position.top, left: position.left }}
      data-testid="note-link-suggestions"
    >
      <div className="p-1.5">
        <div className="flex items-center gap-1.5 px-2 py-1 text-xs text-muted-foreground border-b border-border mb-1">
          <Link2 className="w-3 h-3" />
          <span>Link to note</span>
        </div>
        {filteredNotes.map((note, index) => (
          <button
            key={note.id}
            onClick={() => handleSelect(note)}
            className={`w-full flex items-center gap-2 px-2 py-1.5 text-sm text-left rounded-sm transition-colors ${
              index === selectedIndex
                ? 'bg-accent text-accent-foreground'
                : 'hover:bg-muted'
            }`}
            data-testid={`note-link-option-${note.id}`}
          >
            <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />
            <span className="truncate">{note.title || 'Untitled'}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
